/*
 * Copyright 2026 North Carolina State University
 */

import React from 'react';
import { Box, Button, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';

/**
 * Edit a resource assignment's usage periods: one From/To row per run.
 * Leaving "To" empty means the resource is still in use.
 */
const UsagePeriodsEditor = ({ rows, onChange, disabled = false }) => {
  const update = (index, field, value) => {
    onChange(rows.map((r, i) => (i === index ? { ...r, [field]: value } : r)));
  };

  return (
    <Box>
      {rows.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          No usage dates recorded.
        </Typography>
      )}
      {rows.map((row, index) => (
        // Top-aligned, and every field reserves a helper-text line, so the
        // inputs share one baseline whether or not "To" shows its hint.
        <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap', mb: 1 }}>
          <TextField
            label="From"
            type="date"
            size="small"
            value={row.start_date}
            onChange={(e) => update(index, 'start_date', e.target.value)}
            InputLabelProps={{ shrink: true }}
            helperText=" "
            disabled={disabled}
          />
          <TextField
            label="To"
            type="date"
            size="small"
            value={row.end_date}
            onChange={(e) => update(index, 'end_date', e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: row.start_date || undefined }}
            helperText={row.start_date && !row.end_date ? 'Empty = still in use' : ' '}
            disabled={disabled}
          />
          <TextField
            label="Note"
            size="small"
            value={row.note}
            onChange={(e) => update(index, 'note', e.target.value)}
            placeholder="e.g., Batch 2"
            sx={{ flex: 1, minWidth: 140 }}
            helperText=" "
            disabled={disabled}
          />
          <Tooltip title="Remove these dates">
            <span>
              <IconButton
                size="small"
                color="error"
                onClick={() => onChange(rows.filter((_, i) => i !== index))}
                disabled={disabled}
                sx={{ mt: 0.5 }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      ))}
      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={() => onChange([...rows, { start_date: '', end_date: '', note: '' }])}
        disabled={disabled}
      >
        Add dates
      </Button>
    </Box>
  );
};

export default UsagePeriodsEditor;
